/* Language Mode — Surface Cache Persistence Helpers */
/* (C) 2024 - 2025, by Drew Anderson  - Cloud Media Works, LLC - All Rights Reserved */

// Per-bot storage key — language mode is scoped to each bot, never shared across bots
function languageModeStorageKey(botId: string): string {
  return `ibot.languageMode.${botId}`
}

// Global relay cookie name — always written to the single relay-readable name,
// but refreshed per-connection so it reflects the current bot only.
export const LANGUAGE_MODE_COOKIE_NAME = 'ibot_language_mode'

export function normalizeLanguageMode(language?: string | null): string {
  const cleaned = String(language || '').trim()
  return cleaned || 'English'
}

export function getLanguageMode(botId: string): string {
  return normalizeLanguageMode(localStorage.getItem(languageModeStorageKey(botId)))
}

export function setLanguageMode(botId: string, language: string): void {
  const normalized = normalizeLanguageMode(language)

  localStorage.setItem(languageModeStorageKey(botId), normalized)

  // Write the global relay cookie so the relay server sees the correct language
  // for the current bot on next connection. This is overwritten before each
  // WebSocket connect (connectConversation) so it never bleeds across bots.
  document.cookie = [
    `${LANGUAGE_MODE_COOKIE_NAME}=${encodeURIComponent(normalized)}`,
    'path=/',
    'SameSite=Lax',
    location.protocol === 'https:' ? 'Secure' : '',
    'max-age=31536000',
  ]
    .filter(Boolean)
    .join('; ')
}

export function clearLanguageMode(botId: string): void {
  localStorage.removeItem(languageModeStorageKey(botId))
  document.cookie = `${LANGUAGE_MODE_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}
